// components/DailyReport/DailyReportList.tsx
import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  CircularProgress,
  useMediaQuery,
  Box,
  TablePagination,
  Button,
  Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { ReportListItem } from '../../types/dailyReport';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonIcon from '@mui/icons-material/Person';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Collapse from '@mui/material/Collapse';

interface DailyReportListProps {
  reports: ReportListItem[];
  loading: boolean;
  error: string | null;
  page: number;
  rowsPerPage: number;
  expandedRows: number[];
  onToggleRowExpansion: (index: number) => void;
  onEdit: (id: number) => void;
  onDelete: (id: number) => void;
  onPageChange: (event: unknown, newPage: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onNewClick: () => void;
}

export const DailyReportList: React.FC<DailyReportListProps> = ({
  reports,
  loading,
  error,
  page,
  rowsPerPage,
  expandedRows,
  onToggleRowExpansion,
  onEdit,
  onDelete,
  onPageChange,
  onRowsPerPageChange,
  onNewClick,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  const getTotalAmount = (report: ReportListItem): number => {
    return (report.laborCost || 0) +
      (report.fuelCost || 0) +
      (report.vehicleCost || 0) +
      (report.tollLoadFee || 0);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).replace(/\//g, '-');  // スラッシュをハイフンに変換
  };

  // onPageChange関数を修正
  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    // 画面上部へスクロール
    window.scrollTo({
      top: 0,
      behavior: 'smooth' // スムーズなスクロールを実現
    });

    // 既存のページ変更処理を呼び出す
    onPageChange(event, newPage);
  };

  const renderMobileView = () => {
    const paginatedReports = reports
      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
      <Box>
        {paginatedReports.map((report, index) => (
          <Paper
            key={report.id}
            elevation={2}
            sx={{
              mb: 2,
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              borderLeft: '4px solid',
              borderColor: 'primary.main',
            }}
          >
            <Box
              onClick={() => onToggleRowExpansion(index)}
              sx={{ cursor: 'pointer' }}
            >
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'auto 1fr',  // 日付部分を自動的な幅に
                gap: 3,
                alignItems: 'center',
                mb: 2
              }}>
                <Box>
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'primary.main',
                      fontWeight: 500,
                      fontSize: '1.1rem',
                      borderBottom: '2px solid',
                      borderColor: 'primary.main',
                      width: 'fit-content',  // コンテンツの幅に合わせる
                      pb: 0.5,
                    }}
                  >
                    {formatDate(report.date)}
                  </Typography>
                </Box>
                <Typography sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5
                }}>
                  <PersonIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                  職長: <span style={{ fontWeight: 500 }}>{report.supervisor}</span>
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: '1.1rem',
                    mb: 1,
                    fontWeight: 500
                  }}
                >
                  {report.customerName}
                </Typography>

                {/* 工事内容を追加 */}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    fontSize: '0.9rem',
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical'
                  }}
                >
                  {report.constructionDetail || '(工事内容なし)'}
                </Typography>


                <Typography
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.95rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}
                >
                  <LocationOnIcon sx={{ fontSize: '1rem' }} />
                  {report.siteName}
                </Typography>
              </Box>

              <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: 2,
                color: 'primary.main'  // アイコンの色を変更
              }}>
                <ExpandMoreIcon
                  sx={{
                    transform: expandedRows.includes(index) ? 'rotate(180deg)' : 'rotate(0)',
                    transition: 'transform 0.3s',
                    fontSize: '1.2rem'  // サイズを少し小さく
                  }}
                />
              </Box>
            </Box>

            <Collapse in={expandedRows.includes(index)}>
              <Box mt={2} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography>人工: {report.manPower}</Typography>
                <Typography>走行距離: {(Number(report.mileage ?? 0)).toFixed(1)} km</Typography>
                <Typography>合計金額: ¥{(report.totalAmount ?? 0).toLocaleString()}</Typography>

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    startIcon={<EditIcon />}
                    variant="outlined"
                    size="small"
                    color="primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      report.id && onEdit(report.id);
                    }}
                  >
                    編集
                  </Button>
                  <Button
                    startIcon={<DeleteIcon />}
                    variant="outlined"
                    size="small"
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation();
                      report.id && onDelete(report.id);
                    }}
                  >
                    削除
                  </Button>
                </Box>
              </Box>
            </Collapse>
          </Paper>
        ))}
      </Box>
    );
  };

  const renderDesktopView = () => (
    <TableContainer component={Paper}>
      <Table sx={{ minWidth: 650 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: '120px' }}>日付</TableCell>
            <TableCell sx={{ width: '200px' }}>顧客名</TableCell>
            <TableCell sx={{ width: '200px' }}>工事内容</TableCell>
            <TableCell sx={{ width: '200px' }}>現場名</TableCell>
            <TableCell sx={{ width: '100px' }}>職長</TableCell>
            <TableCell sx={{ width: '80px' }}>人工</TableCell>
            <TableCell sx={{ width: '100px' }}>走行距離</TableCell>
            <TableCell sx={{ width: '120px' }}>合計金額</TableCell>
            <TableCell sx={{ width: '100px' }}>操作</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {reports && reports.length > 0 ? (
            reports
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((report, index) => (
                <TableRow
                  key={report.id}
                  sx={{
                    '&:nth-of-type(odd)': { backgroundColor: 'rgba(0, 0, 0, 0.02)' },
                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                    height: '60px'  // 行の高さを調整
                  }}
                >
                  <TableCell>{formatDate(report.date)}</TableCell>
                  <TableCell>{report.customerName || '-'}</TableCell>
                  <TableCell>{report.constructionDetail || '-'}</TableCell>
                  <TableCell>{report.siteName || '-'}</TableCell>
                  <TableCell>{report.supervisor || '-'}</TableCell>
                  <TableCell>{report.manPower || '-'}</TableCell>
                  <TableCell>{(Number(report.mileage ?? 0)).toFixed(1)} km</TableCell>
                  <TableCell>¥{(report.totalAmount ?? 0).toLocaleString()}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>  {/* ボタン間の間隔を設定 */}
                      <Tooltip title="編集">
                        <IconButton
                          onClick={() => report.id && onEdit(report.id)}
                          color="primary"
                          size="medium"
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="削除">
                        <IconButton
                          onClick={() => report.id && onDelete(report.id)}
                          color="error"
                          size="medium"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
          ) : (
            <TableRow>
              <TableCell colSpan={8} align="center">
                データがありません
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Button
        variant="contained"
        color="primary"
        onClick={onNewClick}
        fullWidth={isSmallScreen}
        sx={{ mb: 2 }}
      >
        新規登録
      </Button>

      {isSmallScreen ? renderMobileView() : renderDesktopView()}

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={reports.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handlePageChange} // 修正したハンドラーを使用
        onRowsPerPageChange={onRowsPerPageChange}
        sx={{
          '.MuiTablePagination-selectLabel': {
            // 「表示件数」のラベルのみ非表示
            display: { xs: 'none', sm: 'block' }
          },
          '.MuiTablePagination-displayedRows': {
            // 総件数表示を非表示
            display: { xs: 'none', sm: 'block' }
          },
          '.MuiTablePagination-select': {
            // セレクトボックスは表示したまま
            minWidth: { xs: '2rem' }
          },
          '.MuiTablePagination-actions': {
            marginLeft: { xs: 0 }
          },
          // セレクトボックスの余白調整
          '.MuiTablePagination-toolbar': {
            paddingLeft: { xs: 0 },
            minHeight: { xs: '48px' }
          }
        }}
        labelRowsPerPage="表示件数:"
        labelDisplayedRows={({ from, to, count }) =>
          isSmallScreen ? '' : `${from}–${to} / ${count}`
        }
      />
    </Box>
  );
};